import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import PageHero from '@/components/common/PageHero';
import { toast } from 'sonner';
import {
  ticketService,
  type StaffTicket,
  type TicketMessage,
  type TicketStatus,
} from '@/lib/services/ticket.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Caixa } from '@/construtor/caixa';

const statusColors: Record<TicketStatus, string> = {
  OPEN: 'bg-primary',
  IN_PROGRESS: 'bg-warning',
  AWAITING_CUSTOMER: 'bg-warning',
  RESOLVED: 'bg-success',
  CLOSED: 'bg-muted',
};

const statusLabels: Record<TicketStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em Andamento',
  AWAITING_CUSTOMER: 'Aguardando Resposta',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

function getAvatarUrl(nick: string, gameType?: string): string {
  const safe = encodeURIComponent(nick.trim());
  if (!safe) return '';
  if (gameType === 'MINECRAFT') return `/api/v1/minecraft/avatar/${safe}?size=64`;
  return `https://hyvatar.io/render/${safe}?size=64`;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export default function StaffTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { store } = useStore();
  const [ticket, setTicket] = useState<StaffTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [permissions, setPermissions] = useState({
    canReply: false,
    canClaim: false,
    canRelease: false,
    canClose: false,
    canViewAll: false,
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    if (store?.id && isAuthenticated && ticketId) {
      loadTicketDetails();
    }
  }, [store?.id, isAuthenticated, ticketId]);

  const loadTicketDetails = async () => {
    if (!store?.id || !ticketId) return;
    try {
      setLoading(true);
      const data = await ticketService.staffGetTicketDetails(store.id, ticketId);
      setTicket(data.ticket);
      setMessages(data.messages);
      setPermissions(data.permissions);
    } catch {
      toast.error('Erro ao carregar detalhes do ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!store?.id || !ticketId) return;
    try {
      setClaiming(true);
      await ticketService.staffClaimTicket(store.id, ticketId);
      toast.success('Ticket atribuído a você');
      loadTicketDetails();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao atribuir ticket');
    } finally {
      setClaiming(false);
    }
  };

  const handleRelease = async () => {
    if (!store?.id || !ticketId) return;
    try {
      setReleasing(true);
      await ticketService.staffReleaseTicket(store.id, ticketId);
      toast.success('Ticket devolvido para a fila');
      loadTicketDetails();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao liberar ticket');
    } finally {
      setReleasing(false);
    }
  };

  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!store?.id || !ticketId) return;
    try {
      setUpdatingStatus(true);
      await ticketService.staffUpdateStatus(store.id, ticketId, newStatus);
      toast.success('Status atualizado');
      loadTicketDetails();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store?.id || !ticketId || !replyMessage.trim()) return;

    try {
      setSending(true);
      await ticketService.staffReplyToTicket(store.id, ticketId, { message: replyMessage });
      setReplyMessage('');
      toast.success('Resposta enviada');
      loadTicketDetails();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao enviar resposta');
    } finally {
      setSending(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div>
        <PageHero
          title="Atendimento"
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Atendimento', href: '/suporte/staff' },
            { label: 'Ticket' },
          ]}
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Você precisa estar logado.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <PageHero
          title="Carregando..."
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Atendimento', href: '/suporte/staff' },
            { label: 'Ticket' },
          ]}
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">Carregando...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div>
        <PageHero
          title="Ticket não encontrado"
          breadcrumbs={[
            { label: 'Suporte', href: '/suporte' },
            { label: 'Atendimento', href: '/suporte/staff' },
          ]}
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Este ticket não existe ou você não tem permissão.</p>
              <Button className="mt-4" onClick={() => navigate('/suporte/staff')}>
                Voltar ao Painel
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title={ticket.title}
        breadcrumbs={[
          { label: 'Suporte', href: '/suporte' },
          { label: 'Atendimento', href: '/suporte/staff' },
          { label: `#${ticket.id.slice(0, 8)}` },
        ]}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/suporte/staff')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar ao Painel
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Caixa id="suporte.equipe.detalhes" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-display">Detalhes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">ID</Label>
                  <p className="font-mono text-sm">#{ticket.id.slice(0, 8)}</p>
                </div>

                <Separator />

                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge className={statusColors[ticket.status]}>
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Atribuição</Label>
                  <div className="mt-1">
                    {ticket.isClaimedByMe ? (
                      <Badge variant="outline" className="border-green-500 text-green-600">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Atribuído a mim
                      </Badge>
                    ) : ticket.isClaimedByOther ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        Em atendimento por outro membro
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-orange-400 text-orange-500">
                        Na fila (sem atribuição)
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-muted-foreground">Criado em</Label>
                  <p className="text-sm">
                    {format(new Date(ticket.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>

                {ticket.closedAt && (
                  <div>
                    <Label className="text-muted-foreground">Fechado em</Label>
                    <p className="text-sm">
                      {format(new Date(ticket.closedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                )}

                <Separator />

                {/* Actions */}
                <div className="space-y-2">
                  {permissions.canClaim && (
                    <Button
                      className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
                      onClick={handleClaim}
                      disabled={claiming}
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      {claiming ? 'Atribuindo...' : 'Atribuir a mim'}
                    </Button>
                  )}

                  {permissions.canRelease && (
                    <Button
                      variant="outline"
                      className="w-full border-orange-400 text-orange-600 hover:bg-orange-50"
                      onClick={handleRelease}
                      disabled={releasing}
                    >
                      <UserX className="w-4 h-4 mr-2" />
                      {releasing ? 'Liberando...' : 'Devolver para a fila'}
                    </Button>
                  )}

                  {permissions.canClose && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs">Alterar status</Label>
                      <Select
                        onValueChange={(v) => handleStatusChange(v as TicketStatus)}
                        disabled={updatingStatus}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecionar ação..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ticket.status === 'OPEN' && (
                            <SelectItem value="IN_PROGRESS">Iniciar atendimento</SelectItem>
                          )}
                          {(ticket.status === 'IN_PROGRESS' || ticket.status === 'AWAITING_CUSTOMER') && (
                            <>
                              <SelectItem value="AWAITING_CUSTOMER">Aguardar cliente</SelectItem>
                              <SelectItem value="RESOLVED">Marcar como resolvido</SelectItem>
                            </>
                          )}
                          {ticket.status !== 'CLOSED' && (
                            <SelectItem value="CLOSED">Fechar ticket</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            </Caixa>
          </div>

          {/* Messages Thread */}
          <div className="lg:col-span-2 space-y-4">
            <Caixa id="suporte.equipe.conversa" miolo>
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-display">{ticket.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex gap-3 ${message.isStaff ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <Avatar className="h-9 w-9">
                          {message.authorNick && (
                            <AvatarImage
                              src={getAvatarUrl(message.authorNick, store?.gameType)}
                              alt={message.authorNick}
                            />
                          )}
                          <AvatarFallback
                            className={
                              message.isStaff
                                ? 'bg-primary/20 text-primary'
                                : 'bg-muted-foreground/20'
                            }
                          >
                            {message.isStaff ? (
                              <ShieldCheck className="h-4 w-4 text-primary" />
                            ) : (
                              <span className="text-xs font-semibold">
                                {getInitials(message.authorName)}
                              </span>
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-[10px] text-muted-foreground font-medium leading-none max-w-[60px] text-center truncate">
                          {message.authorNick ?? message.authorName}
                        </span>
                      </div>

                      <div
                        className={`flex-1 space-y-1 ${message.isStaff ? 'text-right' : 'text-left'}`}
                      >
                        <div
                          className={`inline-block p-3 rounded-lg ${
                            message.isStaff ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(message.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                            locale: ptBR,
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {messages.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Nenhuma mensagem ainda.</p>
                  )}
                </div>

                <Separator />

                {/* Reply area */}
                {ticket.isClosed ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>Este ticket está fechado.</p>
                  </div>
                ) : permissions.canReply ? (
                  <Caixa id="suporte.equipe.responder">
                  <form onSubmit={handleSendReply} className="space-y-4">
                    <div>
                      <Label htmlFor="reply">Sua resposta</Label>
                      <Textarea
                        id="reply"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Digite sua resposta ao cliente..."
                        rows={4}
                        disabled={sending}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={sending || !replyMessage.trim()}
                        className="bg-primary hover:bg-primary-hover text-primary-foreground"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {sending ? 'Enviando...' : 'Enviar Resposta'}
                      </Button>
                    </div>
                  </form>
                  </Caixa>
                ) : ticket.isClaimedByOther ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="mb-1">Este ticket está sendo atendido por outro membro.</p>
                    <p className="text-sm">O atendente deve devolver o ticket para a fila antes de outra pessoa poder atendê-lo.</p>
                  </div>
                ) : permissions.canClaim ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="mb-3">Atribua este ticket a você para poder responder.</p>
                    <Button
                      onClick={handleClaim}
                      disabled={claiming}
                      className="bg-primary hover:bg-primary-hover text-primary-foreground"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      {claiming ? 'Atribuindo...' : 'Atribuir a mim'}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>Você não tem permissão para responder a este ticket.</p>
                  </div>
                )}
              </CardContent>
            </Card>
            </Caixa>
          </div>
        </div>
      </div>
    </div>
  );
}
